import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import '../../../../styles/ScheduleTable.css';
import '../../../../styles/Modal.css';
import { useSport } from '../../../../context/SportContext.tsx';
import { Schedule } from '../../../../models/Schedule.tsx';
import { CommercialLiveComponent } from './CommercailLiveComponent.tsx';
import CommercialLiveSkeleton, { CommercialLiveEmptyLeague } from './CommercialLiveSkeleton.tsx';
import { scheduleLive } from '../../../../services/player/apis/Schelude.tsx';
import AlertError from '../../../alerts/AlertError.tsx';
import { TableComponentsLive } from './TableComponents.tsx';
import { useModal } from '../../../../context/ModalContext.tsx';
import { BetDetails } from '../../../../models/MyBetModel.tsx';
import { openStraightBetSlipOnSelect } from '../../../../helpers/openBetSlipMode.tsx';
import { useVisibilityAdaptiveInterval } from '../../../../hooks/useVisibilityAdaptiveInterval.ts';
import { getLiveScheduleDateRange, pickLiveSlateGames } from '../../../../helpers/scheduleDateRange.ts';
import { handleScheduleColumnToggle } from '../../../../helpers/scheduleColumnToggle.ts';
import {
    GOLF_PGA_LIVE_KEY,
    isGolfFuturesLiveWindow,
    isPgaLiveLeagueKey,
    isPgaStraightGame,
} from '../../../../helpers/golfFuturesLive.ts';
import CommercialGolfFutures from './CommercialGolfFutures.tsx';
import { FaChevronDown } from 'react-icons/fa';
import { getDistanceFromPageBottom, getPageScrollTop } from '../../../../helpers/scrollPageToTop.ts';


const nbaFirstHalfMarkets = [
    "1st_half_total_points",
    "1st_half_team_total",
    "1st_half_point_spread",
    "1st_half_moneyline"
];

const isActiveGame = (game: Schedule) => game.status !== "completed" && game.status !== "cancelled";

const mapCommercialGamesToShow = (games: Schedule[] | undefined | null): Schedule[] => {
    if (!games || games.length === 0) return [];
    return games
        .filter(isActiveGame)
        .map((g: any) => ({
            ...g,
            odds: g.odds?.map((o: any) => ({ ...o })) || [],
            home_competitors: g.home_competitors,
            away_competitors: g.away_competitors,
            leagueLogo: g.leagueLogo,
        }));
};

function CommercialLive() {

    const {
        commercialGames,
        setCommercialGames,
        loading,
        checkedLeagues,
        commercialLeagueOrder,
        clearCommercialGolfFutures,
        sports,
        loadSports,
        checkedLeaguesRef,
    } = useSport();

    const golfPgaLiveChecked = checkedLeagues?.[GOLF_PGA_LIVE_KEY] === true;
    const showGolfFutures =
        golfPgaLiveChecked && isGolfFuturesLiveWindow();
    const [showgames, setShowGames] = useState<Schedule[]>(() => mapCommercialGamesToShow(commercialGames));
    const [liveScoresSynced, setLiveScoresSynced] = useState(false);
    const [, setShowLimitAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingBet, setLoadingBet] = useState(false);
    const downArrowRef = useRef<HTMLButtonElement>(null);
    const bottomSentinelRef = useRef<HTMLDivElement>(null);
    const sportSectionIdsRef = useRef<string[]>([]);
    const atBottomRef = useRef(false);
    const { showModal, updateModalContent } = useModal();

    const onShowBetsModal = (incomingBets?: BetDetails[]) => {
        openStraightBetSlipOnSelect({
            incomingBets,
            showModal,
            updateModalContent,
            setShowLimitAlert,
            setErrorMessage,
        });
    };

    const commercialGamesRef = useRef(commercialGames);
    commercialGamesRef.current = commercialGames;

    const isLiveLeagueChecked = (leagueKey?: string) =>
        Boolean(leagueKey && checkedLeaguesRef?.current?.[leagueKey] === true);

    useEffect(() => {
        setLiveScoresSynced(false);
    }, []);

    useEffect(() => {
        setShowGames(
            mapCommercialGamesToShow(commercialGames).filter(
                g => isLiveLeagueChecked(g.leagueKey)
            )
        );
    }, [commercialGames, checkedLeagues]);

    useEffect(() => {
        if (!sports || sports.length === 0) {
            loadSports?.();
        }
    }, [sports, loadSports]);

    useEffect(() => {
        if (!showGolfFutures) {
            clearCommercialGolfFutures?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showGolfFutures]);

    useVisibilityAdaptiveInterval(
        async () => {
            const games = commercialGamesRef.current;
            if (!games || games.length === 0) return;

            try {
                const uniqueKeys = Array.from(
                    new Set(
                        games
                            .map((g: any) => g.leagueKey as string)
                            .filter((key: string) => isLiveLeagueChecked(key))
                    )
                );
                if (uniqueKeys.length === 0) return;

                const fetchedKeys = new Set(uniqueKeys);
                const isMain = true;
                const results = await Promise.all(
                    uniqueKeys.map(async (key) => {
                        if (!isLiveLeagueChecked(key)) return [];

                        const game = games.find((g: any) =>
                            g.leagueKey === key && g.sport?.name && g.league?.name
                        );
                        if (!game?.sport?.name || !game?.league?.name) return [];

                        const marketId =
                            key.endsWith("1stHalf")
                                ? nbaFirstHalfMarkets
                                : "";

                        const { startDateAfter, startDateBefore, timeOffset } = getLiveScheduleDateRange();
                        const res = await scheduleLive({
                            sport: game.sport.name,
                            league: game.league.name,
                            marketId,
                            isMain,
                            id: undefined,
                            startDateAfter,
                            startDateBefore,
                            timeOffset
                        });
                        return pickLiveSlateGames(res).map((g: any) => ({
                            ...g,
                            leagueKey: key
                        }));
                    })
                );

                let todayGames = results.flat().filter(isActiveGame);

                const uniqueMap = new Map<string, Schedule>();

                todayGames.forEach(g => {
                    const key = `${g.id}-${g.leagueKey}`;
                    uniqueMap.set(key, g);
                });

                todayGames = Array.from(uniqueMap.values())
                    .filter(g => isLiveLeagueChecked(g.leagueKey));

                setShowGames(prevGames => {
                    const prevMap = new Map(
                        prevGames.map(g => [`${g.id}-${g.leagueKey}`, g])
                    );

                    const polledMerged: Schedule[] = todayGames.map(newGame => {
                        const mapKey = `${newGame.id}-${newGame.leagueKey}`;
                        const oldGame = prevMap.get(mapKey);

                        const oldOdds = oldGame?.odds || [];
                        const newOdds = newGame.odds || [];

                        let oddsChanged = false;

                        const mergedOdds = newOdds.map((n: any) => {
                            const existing = oldOdds.find(o => o.id === n.id);

                            if (!existing || existing.price !== n.price || existing.points !== n.points) {
                                oddsChanged = true;
                                return { ...n };
                            }

                            return existing;
                        });

                        const statusChanged =
                            !oldGame ||
                            oldGame.status !== newGame.status ||
                            oldGame.is_live !== newGame.is_live ||
                            oldGame.result?.in_play_data?.clock !== newGame.result?.in_play_data?.clock ||
                            oldGame.result?.in_play_data?.period !== newGame.result?.in_play_data?.period ||
                            oldGame.result?.scores?.home?.total !== newGame.result?.scores?.home?.total ||
                            oldGame.result?.scores?.away?.total !== newGame.result?.scores?.away?.total;

                        if (statusChanged || oddsChanged || !oldGame) {
                            return {
                                ...newGame,
                                odds: mergedOdds,
                                home_competitors: oldGame?.home_competitors || newGame.home_competitors,
                                away_competitors: oldGame?.away_competitors || newGame.away_competitors,
                                leagueLogo: oldGame?.leagueLogo || newGame.leagueLogo,
                                leagueKey: newGame.leagueKey
                            };
                        }

                        return oldGame;
                    });

                    const kept = prevGames.filter(g =>
                        isLiveLeagueChecked(g.leagueKey) && !fetchedKeys.has(g.leagueKey ?? "")
                    );

                    return [...kept, ...polledMerged];
                });

                setCommercialGames(prev => {
                    const kept = prev.filter(g =>
                        isLiveLeagueChecked(g.leagueKey) && !fetchedKeys.has(g.leagueKey ?? "")
                    );

                    const prevMap = new Map(
                        prev.map(g => [`${g.id}-${g.leagueKey}`, g])
                    );

                    const polledMerged: Schedule[] = todayGames.map(newGame => {
                        const oldGame = prevMap.get(`${newGame.id}-${newGame.leagueKey}`);
                        return {
                            ...newGame,
                            home_competitors: oldGame?.home_competitors || newGame.home_competitors,
                            away_competitors: oldGame?.away_competitors || newGame.away_competitors,
                            leagueLogo: oldGame?.leagueLogo || newGame.leagueLogo,
                            leagueKey: newGame.leagueKey
                        };
                    });

                    return [...kept, ...polledMerged];
                });

                setLiveScoresSynced(true);
            } catch (err) {
                console.error("Error updating games:", err);
            }
        },
        Boolean(commercialGames && commercialGames.length > 0),
        { activeMs: 4000, pauseWhenHidden: true, runImmediately: true }
    );

    const validGames = useMemo(
        () =>
            showgames.filter(
                (g) =>
                    !isPgaStraightGame(g) &&
                    g?.sport?.name &&
                    g?.league?.name &&
                    g?.home_team_display &&
                    g?.away_team_display
            ),
        [showgames]
    );

    const sortedGroups = useMemo(() => {
        const grouped = validGames.reduce((acc, game) => {
            const d = new Date(game.start_date);
            const key = `${game.league?.id}-${game.leagueKey}-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            (acc[key] ??= []).push(game);
            return acc;
        }, {} as Record<string, Schedule[]>);

        return Object.entries(grouped).sort((a, b) => {
            const leagueKeyA = a[1][0].leagueKey ?? "";
            const leagueKeyB = b[1][0].leagueKey ?? "";
            const orderA = (commercialLeagueOrder ?? []).indexOf(leagueKeyA);
            const orderB = (commercialLeagueOrder ?? []).indexOf(leagueKeyB);
            const rankA = orderA === -1 ? Number.MAX_SAFE_INTEGER : orderA;
            const rankB = orderB === -1 ? Number.MAX_SAFE_INTEGER : orderB;

            if (rankA !== rankB) return rankA - rankB;

            return new Date(a[1][0].start_date).getTime() - new Date(b[1][0].start_date).getTime();
        });
    }, [validGames, commercialLeagueOrder]);

    const sportSectionIds = useMemo(() => {
        const ids: string[] = [];
        const seen = new Set<string>();
        sortedGroups.forEach(([, games], index) => {
            const key = games[0]?.leagueKey ?? "";
            if (!key || seen.has(key)) return;
            seen.add(key);
            ids.push(`live-league-section-${index}`);
        });
        if (showGolfFutures) {
            ids.push("live-league-section-pga");
        }
        return ids;
    }, [sortedGroups, showGolfFutures]);

    sportSectionIdsRef.current = sportSectionIds;

    const jumpToNextSport = () => {
        const sections = sportSectionIdsRef.current
            .map(id => document.getElementById(id))
            .filter((el): el is HTMLElement => Boolean(el));
        if (sections.length < 2) return;

        const anchor = 120;
        const scrollTop = getPageScrollTop();

        let currentIndex = 0;
        for (let i = 0; i < sections.length; i++) {
            if (sections[i].getBoundingClientRect().top <= anchor + 10) {
                currentIndex = i;
            }
        }

        // At page top the first league is already visible — skip it.
        const nextIndex =
            scrollTop < anchor && currentIndex === 0 ? 1 : currentIndex + 1;

        if (nextIndex >= sections.length) return;

        sections[nextIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    useLayoutEffect(() => {
        const btn = downArrowRef.current;
        const sentinel = bottomSentinelRef.current;
        if (!btn || !sentinel || sportSectionIds.length < 2) return;

        const getDistanceFromBottom = () => getDistanceFromPageBottom();

        const setArrowVisible = (visible: boolean) => {
            btn.classList.toggle('live-next-sport--hidden', !visible);
            btn.tabIndex = visible ? 0 : -1;
            btn.setAttribute('aria-hidden', visible ? 'false' : 'true');
        };

        const syncArrowVisibility = () => {
            setArrowVisible(!atBottomRef.current);
        };

        const updateAtBottom = () => {
            atBottomRef.current = getDistanceFromBottom() <= 12;
            syncArrowVisibility();
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    atBottomRef.current = true;
                } else if (getDistanceFromBottom() > 16) {
                    atBottomRef.current = false;
                }
                syncArrowVisibility();
            },
            {
                root: document.querySelector(".commercial-live-surface"),
                threshold: [0, 1],
            },
        );

        observer.observe(sentinel);
        updateAtBottom();

        const liveSurface = document.querySelector(".commercial-live-surface");
        liveSurface?.addEventListener("scroll", updateAtBottom, { passive: true });
        document.addEventListener('scroll', updateAtBottom, {
            capture: true,
            passive: true,
        });
        window.addEventListener('scroll', updateAtBottom, { passive: true });
        window.addEventListener('resize', updateAtBottom);
        window.visualViewport?.addEventListener('resize', updateAtBottom);

        return () => {
            observer.disconnect();
            liveSurface?.removeEventListener("scroll", updateAtBottom);
            document.removeEventListener('scroll', updateAtBottom, true);
            window.removeEventListener('scroll', updateAtBottom);
            window.removeEventListener('resize', updateAtBottom);
            window.visualViewport?.removeEventListener('resize', updateAtBottom);
        };
    }, [sportSectionIds, validGames, showGolfFutures, loading]);

    const pendingLiveLeagues = useMemo(() => {
        return (commercialLeagueOrder ?? [])
            .filter((k: string) => checkedLeagues[k] === true && (k.endsWith("-Live") || k.endsWith("-1stHalf")))
            .filter((k: string) => !isPgaLiveLeagueKey(k))
            .filter((k: string) => !validGames.some(g => g.leagueKey === k))
            .map((k: string) => {
                const isFirstHalf = k.endsWith("-1stHalf");
                const withoutSuffix = k.replace(/-Live$|-1stHalf$/, "");
                const dash = withoutSuffix.indexOf("-");
                return {
                    leagueName: dash >= 0 ? withoutSuffix.slice(dash + 1) : withoutSuffix,
                    isFirstHalf,
                };
            });
    }, [commercialLeagueOrder, checkedLeagues, validGames]);

    return (
        <>

            {validGames.length > 0 && (
                <div className="containertable">
                    {sortedGroups.map(([groupKey, games], groupIndex) => {
                        const firstGame = games[0];
                        const formattedDate = new Date(firstGame.start_date).toLocaleDateString('en-US', {
                            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                        }).toUpperCase();

                        return (
                            <div
                                key={groupKey}
                                id={`live-league-section-${groupIndex}`}
                                className="league-group"
                            >
                                <TableComponentsLive key={`${games[0].leagueKey}-header`} game={games[0]} onToggleColumns={handleScheduleColumnToggle} formattedDate={formattedDate} />

                                {games.map(game => (
                                    <CommercialLiveComponent
                                        key={`${game.id}-${game.leagueKey}`}
                                        game={game}
                                        liveScoresSynced={liveScoresSynced}
                                        onShowBetsModal={onShowBetsModal}
                                        setLoadingBet={setLoadingBet}
                                        loadingBet={loadingBet}
                                        setErrorMessage={setErrorMessage}
                                    />
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            {sportSectionIds.length > 1 && (
                <button
                    ref={downArrowRef}
                    type="button"
                    className="live-next-sport live-next-sport--hidden"
                    onClick={jumpToNextSport}
                    aria-label="Next sport"
                    aria-hidden="true"
                    tabIndex={-1}
                >
                    <FaChevronDown />
                </button>
            )}

            {loading && pendingLiveLeagues.length > 0 && (
                <CommercialLiveSkeleton leagues={pendingLiveLeagues} rows={3} />
            )}

            {!loading && pendingLiveLeagues.map((league) => (
                <CommercialLiveEmptyLeague
                    key={`${league.leagueName}-${league.isFirstHalf ? "1stHalf" : "Live"}`}
                    leagueName={league.leagueName}
                    isFirstHalf={league.isFirstHalf}
                />
            ))}

            {!loading && golfPgaLiveChecked && !showGolfFutures && (
                <CommercialLiveEmptyLeague leagueName="PGA" />
            )}

            {showGolfFutures && (
                <div id="live-league-section-pga">
                    <CommercialGolfFutures
                        onShowBetsModal={onShowBetsModal}
                        setErrorMessage={setErrorMessage}
                    />
                </div>
            )}

            {errorMessage && (
                <AlertError
                    message={errorMessage}
                    onClose={() => setErrorMessage("")}
                />
            )}

            <div
                ref={bottomSentinelRef}
                className="live-scroll-end-sentinel"
                aria-hidden="true"
            />

        </>
    )
}

export default CommercialLive;

